import { lazyRoute as lazy } from "@/lib/routeModuleRecovery";
/* eslint-disable react-refresh/only-export-components */
import type { RouteObject } from "react-router-dom";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Suspense, useEffect, useRef, useState } from "react";
import {
  isLoggedIn,
  getUserRole,
  hasAdminAccess,
  canAccessAdminRoute,
  hasClinicalAccess,
  hasStaffAccess,
  hasMarketingAccess,
  isSuperAdmin,
  isNewClient,
  intakeIsComplete,
} from "../hooks/usePortalAuth";
import { canAccessClientData, canAccessSupervisorDashboard, isParentGuardian } from "../lib/auth";
import {
  DESIGN_PREVIEW_MODE,
  getPreviewRole,
} from "../lib/previewMode";

import { apiGetCurrentUser, apiListSupervisionLogRelationships, PORTAL_ACCESS_UNAVAILABLE_MESSAGE } from "../lib/api";
import { clearStoredProfile, clearStoredRoles, setStoredProfile, setStoredRoles } from "../lib/auth";

// Production pages are loaded at their existing route boundaries. The guards
// below remain in the eagerly loaded shell, so code splitting does not move or
// weaken any authentication or authorization decision.
const NotFound = lazy(() => import("../pages/NotFound"));
const ClientPortalLogin = lazy(() => import("../pages/clientportal/page"));
const PasswordResetPage = lazy(() => import("../pages/clientportal/password-reset"));
const ClientDashboard = lazy(() => import("../pages/clientportal/dashboard/page"));
const AppointmentRequestFlow = lazy(() => import("../pages/clientportal/appointment-request/page"));
const PublicBookingPage = lazy(() => import("../pages/clientportal/booking/page"));
const IntakeChecklistPage = lazy(() => import("../pages/clientportal/intake/page"));
const PaymentStepPage = lazy(() => import("../pages/clientportal/payment/page"));
const AppointmentsPage = lazy(() => import("../pages/clientportal/appointments/page"));
const MessagesPage = lazy(() => import("../pages/clientportal/messages/page"));
const ClientMeasuresPage = lazy(() => import("../pages/clientportal/measures/page"));
const ClientResourcesPage = lazy(() => import("../pages/clientportal/resources/page"));
const ClientSurveyPage = lazy(() => import("../pages/clientportal/surveys/page"));
const DocumentsPage = lazy(() => import("../pages/clientportal/documents/page"));
const BillingPage = lazy(() => import("../pages/clientportal/billing/page"));
const ProfileSettingsPage = lazy(() => import("../pages/clientportal/settings/page"));
const AccountSettingsPage = lazy(() => import("../pages/account/settings/page"));
const OnboardingWelcomePage = lazy(() => import("../pages/clientportal/onboarding/page"));
const StaffInvitePage = lazy(() => import("../pages/staff-invite/page"));
const ClientInvitePage = lazy(() => import("../pages/client-invite/page"));
const EmailVerificationPage = lazy(() => import("../pages/email-verification/page"));
const ReturningClientPage = lazy(() => import("../pages/clientportal/returning-client/page"));

// --- Staff pages ---
const ProductionStaffDashboardPage = lazy(() => import("../pages/staff/ProductionStaffDashboardPage"));
const StaffMessagesPage = lazy(() => import("../pages/staff/messages/page"));
const ProductionSupervisionPage = lazy(() => import("../pages/staff/supervision/ProductionSupervisionPage"));
const StaffResourcesPage = lazy(() => import("../pages/staff/resources/page"));
const StaffAvailabilityPage = lazy(() => import("../pages/staff/availability/page"));
const StaffBookingRequestsPage = lazy(() => import("../pages/staff/booking-requests/page"));
const StaffCancellationRequestsPage = lazy(() => import("../pages/staff/cancellation-requests/page"));
const ProductionStaffClientsPage = lazy(() => import("../pages/staff/clients/ProductionStaffClientsPage"));
const CanonicalClientChartPage = lazy(() => import("../pages/staff/clients/CanonicalClientChartPage"));
const LegacyAdminClientChartRedirect = lazy(() => import("../pages/staff/clients/CanonicalClientChartPage").then((module) => ({ default: module.LegacyAdminClientChartRedirect })));
const NoteEditorPage = lazy(() => import("../pages/staff/clients/NoteEditorPage"));
const ProductionTreatmentPlanEditorPage = lazy(() => import("../pages/staff/clients/ProductionTreatmentPlanEditorPage"));
const ClinicalTemplateDocumentPage = lazy(() => import("../pages/staff/clients/ClinicalTemplateDocumentPage"));
const ProductionDischargeSummaryPage = lazy(() => import("../pages/staff/clients/ProductionDischargeSummaryPage"));
const ProductionClinicalArchitectPage = lazy(() => import("../pages/staff/clinicalarchitect/ProductionClinicalArchitectPage"));
const ClinicianAttendancePage = lazy(() => import("../pages/staff/attendance/page"));
const StaffGroupsPage = lazy(() => import("../pages/staff/groups/page"));
const StaffTasksPage = lazy(() => import("../pages/staff/tasks/page"));
const AnalyticsPage = lazy(() => import("../pages/staff/analytics/page"));
const StandardOperatingProceduresPage = lazy(() => import("../pages/staff/standard-operating-procedures/page"));

// --- Marketing pages ---
const MarketingDashboardPage = lazy(() => import("../pages/marketing/page"));

// --- Admin pages ---
const AdminDashboardPage = lazy(() => import("../pages/admin/page"));
const AdminAppointmentsPage = lazy(() => import("../pages/admin/appointments/page"));
const AdminDocumentsPage = lazy(() => import("../pages/admin/documents/page"));
const AdvancedBillingRecords = lazy(() => import("../pages/admin/billing/records/page"));
const AdminBillingPage = lazy(() => import("../pages/admin/billing/page"));
const AdminOperationsPage = lazy(() => import("../pages/admin/operations/page"));
const AdminMessagesPage = lazy(() => import("../pages/admin/messages/page"));
const AdminAvailabilityPage = lazy(() => import("../pages/admin/availability/page"));
const AdminRolesPage = lazy(() => import("../pages/admin/roles/page"));
const AdminStaffProfilesPage = lazy(() => import("../pages/admin/staff-profiles/page"));

// --- Template / Form pages ---
const AdminFormsPage = lazy(() => import("../pages/admin/forms/page"));
const FormBuilderPage = lazy(() => import("../pages/admin/forms/builder/page"));
const AdminClientRegistryPage = lazy(() => import("../pages/admin/client-registry/page"));
const AdminGroupManagementPage = lazy(() => import("../pages/admin/groups/page"));

// The mock-backed design preview must not become a production bundle
// dependency. Vite replaces import.meta.env.DEV at build time, so the dynamic
// import and its clinical demo data are omitted from production output.
const PreviewClinicalArchitectPage = import.meta.env.DEV
  ? lazy(() => import("../pages/staff/clinicalarchitect/page"))
  : null;
const PreviewSupervisionPage = import.meta.env.DEV
  ? lazy(() => import("../pages/staff/supervision/page"))
  : null;
const PreviewStaffDashboardPage = import.meta.env.DEV
  ? lazy(() => import("../pages/staff/page"))
  : null;
const PreviewClientDetailPage = import.meta.env.DEV
  ? lazy(() => import("../pages/staff/clients/PreviewClientDetailPage"))
  : null;
const PreviewStaffClientsPage = import.meta.env.DEV
  ? lazy(() => import("../pages/staff/clients/page"))
  : null;
const PreviewTreatmentPlanEditorPage = import.meta.env.DEV
  ? lazy(() => import("../pages/staff/clients/TreatmentPlanEditorPage"))
  : null;
const PreviewBookingVisibilityPage = import.meta.env.DEV
  ? lazy(() => import("../pages/admin/booking-visibility/page"))
  : null;
const PreviewServiceSettingsPage = import.meta.env.DEV
  ? lazy(() => import("../pages/admin/service-settings/page"))
  : null;
const PreviewAttendanceTotalsPage = import.meta.env.DEV
  ? lazy(() => import("../pages/admin/attendance/page"))
  : null;
const ProductionAttendanceTotalsPage = lazy(() => import("../pages/admin/attendance/ProductionAttendanceTotalsPage"));
const PracticeSettingsPage = lazy(() => import("../pages/admin/practice-settings/page"));
const ClaimDraftsPage = lazy(() => import("../pages/admin/claim-drafts/page"));
const AdminActivityLogPage = lazy(() => import("../pages/admin/activity-log/page"));

// ============================================================
// Route Guards
// ============================================================
// When DESIGN_PREVIEW_MODE = true, guards use the selected
// preview role instead of real auth state. Flip the flag to
// false to re-activate real authentication checks.
// ============================================================


const PORTAL_REVALIDATION_TTL_MS = 30_000;
const PORTAL_REVALIDATION_TIMEOUT_MS = 10_000;
let lastSuccessfulPortalRevalidationAt = 0;

type ProtectedStaffRole = "admin" | "clinical" | "staff" | "superadmin" | "marketing";
function hasProtectedStaffRole(role: ProtectedStaffRole): boolean {
  return role === "superadmin" ? isSuperAdmin() : role === "marketing" ? hasMarketingAccess()
    : role === "admin" ? hasAdminAccess() : role === "staff" ? hasStaffAccess() : hasClinicalAccess();
}

function ProtectedRevalidationBoundary({ children, allowArchivedLimitedAccess = false, requireCompletedIntake = false, requireClientAccess = false, requiredStaffRole }: { children: React.ReactNode; allowArchivedLimitedAccess?: boolean; requireCompletedIntake?: boolean; requireClientAccess?: boolean; requiredStaffRole?: ProtectedStaffRole }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Browser storage alone is not proof of a live session after a full reload.
  const cachedSessionAvailable = isLoggedIn() && lastSuccessfulPortalRevalidationAt > 0
    && (!requiredStaffRole || hasProtectedStaffRole(requiredStaffRole))
    && (!requireClientAccess || canAccessClientData());
  const [checking, setChecking] = useState(!cachedSessionAvailable);
  const [notice, setNotice] = useState<string | null>(null);
  const [retryVersion, setRetryVersion] = useState(0);
  const initialValidationComplete = useRef(cachedSessionAvailable);

  useEffect(() => {
    if (DESIGN_PREVIEW_MODE) { setChecking(false); return; }
    let cancelled = false;
    let inFlight = false;
    let controller: AbortController | null = null;
    let timeoutId: number | null = null;
    const revalidate = async ({ force = false }: { force?: boolean } = {}) => {
      if (inFlight) return;
      if (!force && initialValidationComplete.current && Date.now() - lastSuccessfulPortalRevalidationAt < PORTAL_REVALIDATION_TTL_MS) return;
      inFlight = true;
      // Focus commonly moves between the portal and embedded provider fields.
      // Keep already-authorized content mounted during background checks so
      // an in-progress secure form is not destroyed before it can be submitted.
      if (!initialValidationComplete.current) setChecking(true);
      controller = new AbortController();
      timeoutId = window.setTimeout(() => controller?.abort(), PORTAL_REVALIDATION_TIMEOUT_MS);
      const response = await apiGetCurrentUser({ signal: controller.signal, cache: "no-store", portalContext: requireClientAccess });
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      timeoutId = null;
      controller = null;
      inFlight = false;
      if (cancelled) return;
      if (!response.data?.user) {
        if (![401,403].includes(response.status)) {
          if (!initialValidationComplete.current) {
            setNotice("Secure portal access is taking longer than expected. Please try again.");
            setChecking(false);
          }
          return;
        }
        clearStoredProfile(); clearStoredRoles();
        lastSuccessfulPortalRevalidationAt = 0;
        const message = response.error?.code === "portal_access_unavailable" ? PORTAL_ACCESS_UNAVAILABLE_MESSAGE : null;
        navigate(`/clientportal?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`, { replace: true, state: { message, portalReturn: { path: location.pathname + location.search + location.hash, createdAt: Date.now() } } });
        return;
      }
      setStoredProfile(response.data.user);
      setStoredRoles(response.data.roles || []);
      if (requiredStaffRole && !hasProtectedStaffRole(requiredStaffRole)) {
        navigate(hasAdminAccess() ? "/admin" : hasClinicalAccess() ? "/staff" : "/clientportal/dashboard", { replace: true });
        return;
      }
      // A session cookie survives refresh/Stripe return even when the browser
      // role cache does not. Fresh server roles remain authoritative.
      if (requireClientAccess && (!canAccessClientData() || (requireCompletedIntake && hasAdminAccess()))) {
        const destination = hasAdminAccess() ? "/admin" : hasClinicalAccess() ? "/staff" : hasMarketingAccess() ? "/marketing" : "/clientportal";
        navigate(destination, { replace: true });
        return;
      }
      if (requireCompletedIntake && isNewClient() && !intakeIsComplete()) {
        navigate("/clientportal/intake", { replace: true });
        return;
      }
      const clientAccess = response.data.client_access as { record_state?: string; archived_self_service_access_enabled?: boolean } | null | undefined;
      if (clientAccess?.record_state === "archived" && !allowArchivedLimitedAccess) {
        navigate("/clientportal/returning-client", { replace: true });
        return;
      }
      lastSuccessfulPortalRevalidationAt = Date.now();
      setNotice(null);
      initialValidationComplete.current = true;
      setChecking(false);
    };
    void revalidate();
    const onFocus = () => { void revalidate(); };
    const onVisibility = () => { if (document.visibilityState === "visible") void revalidate(); };
    const onRevoked = () => { void revalidate({ force: true }); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("portal-auth-revoked", onRevoked);
    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      controller?.abort();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("portal-auth-revoked", onRevoked);
    };
  }, [navigate, location.pathname, location.search, location.hash, allowArchivedLimitedAccess, requireCompletedIntake, requireClientAccess, requiredStaffRole, retryVersion]);

  if (checking) return <div className="min-h-screen bg-brand-bg px-4 py-10 font-manrope text-sm text-brand-dark/60">Checking secure portal access…</div>;
  if (notice) return <div className="min-h-screen bg-brand-bg px-4 py-10 font-manrope text-sm text-brand-dark/60"><p>{notice}</p><button type="button" onClick={() => { setNotice(null); setRetryVersion((version) => version + 1); }} className="mt-4 rounded-full border border-brand-dark/20 bg-white px-4 py-2 font-semibold text-brand-dark">Try again</button></div>;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (DESIGN_PREVIEW_MODE) {
      const pr = getPreviewRole();
      if (pr === "superadmin" || pr === "admin") return;
      if (pr === "clinician" || pr === "supervisor") { navigate("/staff", { replace: true }); return; }
      if (pr === "marketing") { navigate("/marketing", { replace: true }); return; }
      navigate("/clientportal/dashboard", { replace: true });
      return;
    }
  }, [navigate]);
  return DESIGN_PREVIEW_MODE ? <>{children}</> : <ProtectedRevalidationBoundary requiredStaffRole="admin">{children}</ProtectedRevalidationBoundary>;
}

function RequireClientPortal({ children, allowArchivedLimitedAccess=false }: { children: React.ReactNode; allowArchivedLimitedAccess?:boolean }) {
  return <ProtectedRevalidationBoundary requireClientAccess allowArchivedLimitedAccess={allowArchivedLimitedAccess}>{children}</ProtectedRevalidationBoundary>;
}

function RequireAuthenticated({ children }: { children: React.ReactNode }) {
  return <ProtectedRevalidationBoundary>{children}</ProtectedRevalidationBoundary>;
}

function ParentGuardianRoleBoundary({ children }: { children: React.ReactNode }) {
  if (!DESIGN_PREVIEW_MODE && !isParentGuardian()) return <Navigate to="/clientportal/dashboard" replace />;
  return <>{children}</>;
}

function RequireParentGuardian({ children }: { children: React.ReactNode }) {
  return <ProtectedRevalidationBoundary requireClientAccess><ParentGuardianRoleBoundary>{children}</ParentGuardianRoleBoundary></ProtectedRevalidationBoundary>;
}

function RequireEstablished({ children }: { children: React.ReactNode }) {
  return <ProtectedRevalidationBoundary requireClientAccess requireCompletedIntake>{children}</ProtectedRevalidationBoundary>;
}

function RequireClinician({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (DESIGN_PREVIEW_MODE) {
      const pr = getPreviewRole();
      if (pr === "superadmin" || pr === "clinician" || pr === "supervisor") return;
      if (pr === "admin") { navigate("/admin", { replace: true }); return; }
      if (pr === "marketing") { navigate("/marketing", { replace: true }); return; }
      navigate("/clientportal/dashboard", { replace: true });
      return;
    }
  }, [navigate]);
  return DESIGN_PREVIEW_MODE ? <>{children}</> : <ProtectedRevalidationBoundary requiredStaffRole="clinical">{children}</ProtectedRevalidationBoundary>;
}

function RequireStaffCalendar({ children }: { children: React.ReactNode }) {
  return DESIGN_PREVIEW_MODE ? <>{children}</> : <ProtectedRevalidationBoundary requiredStaffRole="staff">{children}</ProtectedRevalidationBoundary>;
}

function RequireSupervisionParticipant({ children }: { children: React.ReactNode }) {
  const content = <SupervisionParticipantBoundary>{children}</SupervisionParticipantBoundary>;
  return DESIGN_PREVIEW_MODE ? content : <ProtectedRevalidationBoundary requiredStaffRole="clinical">{content}</ProtectedRevalidationBoundary>;
}

function SupervisionParticipantBoundary({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(() => DESIGN_PREVIEW_MODE
    ? ["superadmin", "supervisor"].includes(getPreviewRole())
    : canAccessSupervisorDashboard());
  useEffect(() => {
    if (DESIGN_PREVIEW_MODE) {
      if (!authorized) navigate(getPreviewRole() === "clinician" ? "/staff" : "/clientportal/dashboard", { replace: true });
      return;
    }
    if (authorized) return;
    if (!isLoggedIn()) { navigate("/clientportal", { replace: true }); return; }
    if (!hasClinicalAccess()) { navigate("/clientportal/dashboard", { replace: true }); return; }
    if (canAccessSupervisorDashboard()) { setAuthorized(true); return; }
    let active = true;
    void apiListSupervisionLogRelationships().then((response) => {
      if (!active) return;
      if (response.data?.relationships?.length) setAuthorized(true);
      else navigate("/staff", { replace: true });
    });
    return () => { active = false; };
  }, [authorized, navigate]);
  if (!authorized) return <p role="status" className="p-6 text-sm text-brand-dark/55">Checking supervision access…</p>;
  return <>{children}</>;
}

function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (DESIGN_PREVIEW_MODE) {
      const pr = getPreviewRole();
      if (pr === "superadmin") return;
      navigate("/admin", { replace: true });
      return;
    }
  }, [navigate]);
  return DESIGN_PREVIEW_MODE ? <>{children}</> : <ProtectedRevalidationBoundary requiredStaffRole="superadmin">{children}</ProtectedRevalidationBoundary>;
}

function RequireMarketing({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (DESIGN_PREVIEW_MODE) {
      const pr = getPreviewRole();
      if (pr === "superadmin" || pr === "marketing") return;
      if (pr === "admin") { navigate("/admin", { replace: true }); return; }
      if (pr === "clinician" || pr === "supervisor") { navigate("/staff", { replace: true }); return; }
      navigate("/clientportal/dashboard", { replace: true });
      return;
    }
  }, [navigate]);
  return DESIGN_PREVIEW_MODE ? <>{children}</> : <ProtectedRevalidationBoundary requiredStaffRole="marketing">{children}</ProtectedRevalidationBoundary>;
}

const routes: RouteObject[] = [
  {
    path: "/staff-invite/:token",
    element: <StaffInvitePage />,
  },
  {
    path: "/client-invite/:token",
    element: <ClientInvitePage />,
  },
  // ─── Root → Portal Login ───────────────────────────────
  {
    path: "/",
    element: <ClientPortalLogin />,
  },

  // ─── Client Portal ───────────────────────────────────────
  {
    path: "/clientportal",
    element: <ClientPortalLogin />,
  },
  {
    path: "/reset-password",
    element: <PasswordResetPage />,
  },
  { path: "/verify-email", element: <EmailVerificationPage /> },
  {
    path: "/clientportal/login",
    element: <Navigate to="/clientportal" replace />,
  },
  {
    path: "/clientportal/returning-client",
    element: (
      <RequireClientPortal>
        <ProtectedRevalidationBoundary allowArchivedLimitedAccess>
          <ReturningClientPage />
        </ProtectedRevalidationBoundary>
      </RequireClientPortal>
    ),
  },
  {
    path: "/clientportal/dashboard",
    element: (
      <RequireEstablished>
        <ClientDashboard />
      </RequireEstablished>
    ),
  },
  {
    path: "/clientportal/appointments",
    element: (
      <RequireClientPortal allowArchivedLimitedAccess>
        <AppointmentsPage />
      </RequireClientPortal>
    ),
  },
  {
    path: "/clientportal/appointment-request",
    element: (
      <RequireEstablished>
        <AppointmentRequestFlow />
      </RequireEstablished>
    ),
  },
  // Public prospective-client booking — no guard, no auth required
  {
    path: "/booking",
    element: <PublicBookingPage routeContext="public" />,
  },
  {
    path: "/clientportal/booking",
    element: <Navigate to="/clientportal/appointment-request" replace />,
  },
  {
    path: "/clientportal/intake",
    element: (
      <RequireClientPortal allowArchivedLimitedAccess>
        <IntakeChecklistPage />
      </RequireClientPortal>
    ),
  },
  {
    path: "/clientportal/payment",
    element: (
      <RequireClientPortal>
        <PaymentStepPage />
      </RequireClientPortal>
    ),
  },
  {
    path: "/clientportal/messages",
    element: (
      <RequireClientPortal>
        <MessagesPage />
      </RequireClientPortal>
    ),
  },
  {
    path: "/clientportal/documents",
    element: (
      <RequireEstablished>
        <DocumentsPage />
      </RequireEstablished>
    ),
  },
  {
    path: "/clientportal/measures",
    element: (
      <RequireClientPortal>
        <ClientMeasuresPage />
      </RequireClientPortal>
    ),
  },
  {
    path: "/clientportal/surveys",
    element: (
      <RequireClientPortal>
        <ClientSurveyPage />
      </RequireClientPortal>
    ),
  },
  {
    path: "/clientportal/surveys/:surveyId",
    element: (
      <RequireClientPortal>
        <ClientSurveyPage />
      </RequireClientPortal>
    ),
  },
  {
    path: "/clientportal/resources",
    element: (
      <RequireEstablished>
        <ClientResourcesPage />
      </RequireEstablished>
    ),
  },
  {
    path: "/clientportal/billing",
    element: (
      <RequireClientPortal allowArchivedLimitedAccess>
        <BillingPage />
      </RequireClientPortal>
    ),
  },
  {
    path: "/clientportal/settings",
    element: (
      <RequireClientPortal allowArchivedLimitedAccess>
        <ProfileSettingsPage />
      </RequireClientPortal>
    ),
  },
  {
    path: "/account/settings",
    element: <RequireAuthenticated><AccountSettingsPage /></RequireAuthenticated>,
  },
  {
    path: "/clientportal/onboarding",
    element: (
      <RequireClientPortal>
        <OnboardingWelcomePage />
      </RequireClientPortal>
    ),
  },
  {
    path: "/clientportal/onboarding/*",
    element: <Navigate to="/clientportal/onboarding" replace />,
  },

  // ─── Parent / Guardian Portal ────────────────────────────
  {
    path: "/clientportal/parent",
    element: (
      <RequireParentGuardian>
        <Navigate to="/clientportal/dashboard" replace />
      </RequireParentGuardian>
    ),
  },

  // ─── Legacy redirects (/clientportal/admin → /admin) ─────
  { path: "/clientportal/admin", element: <Navigate to="/admin" replace /> },
  { path: "/clientportal/admin/appointments", element: <Navigate to="/admin/appointments" replace /> },
  { path: "/clientportal/admin/documents", element: <Navigate to="/admin/documents" replace /> },
  { path: "/clientportal/admin/billing", element: <Navigate to="/admin/billing" replace /> },
  { path: "/clientportal/admin/messages", element: <Navigate to="/admin/messages" replace /> },
  { path: "/clientportal/admin/availability", element: <Navigate to="/admin/availability" replace /> },
  { path: "/clientportal/admin/open-slots", element: <Navigate to="/admin/availability" replace /> },
  { path: "/clientportal/admin/availability-overview", element: <Navigate to="/admin/availability" replace /> },
  { path: "/clientportal/admin/roles", element: <Navigate to="/admin/staff-profiles" replace /> },
  { path: "/clientportal/admin/*", element: <Navigate to="/admin" replace /> },
  // Legacy redirects (/clientportal/clinical → /staff)
  { path: "/clientportal/clinical", element: <Navigate to="/staff" replace /> },
  { path: "/clientportal/clinical/messages", element: <Navigate to="/staff/messages" replace /> },
  { path: "/clientportal/supervisor", element: <Navigate to="/staff/supervision" replace /> },

  // ─── Staff (top-level) ───────────────────────────────────
  {
    path: "/staff",
    element: (
      <RequireClinician>
        {DESIGN_PREVIEW_MODE && PreviewStaffDashboardPage
          ? <Suspense fallback={<div className="p-8">Loading design preview…</div>}><PreviewStaffDashboardPage /></Suspense>
          : <ProductionStaffDashboardPage />}
      </RequireClinician>
    ),
  },
  {
    path: "/staff/messages",
    element: (
      <RequireStaffCalendar>
        <StaffMessagesPage />
      </RequireStaffCalendar>
    ),
  },
  {
    path: "/staff/tasks",
    element: (
      <RequireStaffCalendar>
        <StaffTasksPage />
      </RequireStaffCalendar>
    ),
  },
  {
    path: "/staff/analytics",
    element: (
      <RequireStaffCalendar>
        <AnalyticsPage />
      </RequireStaffCalendar>
    ),
  },
  {
    path: "/staff/standard-operating-procedures",
    element: (
      <RequireStaffCalendar>
        <StandardOperatingProceduresPage />
      </RequireStaffCalendar>
    ),
  },
  {
    path: "/staff/supervision",
    element: (
      <RequireSupervisionParticipant>
        {DESIGN_PREVIEW_MODE && PreviewSupervisionPage
          ? <Suspense fallback={<div className="p-8">Loading design preview…</div>}><PreviewSupervisionPage /></Suspense>
          : <ProductionSupervisionPage />}
      </RequireSupervisionParticipant>
    ),
  },
  {
    path: "/staff/availability",
    element: (
      <RequireStaffCalendar>
        <StaffAvailabilityPage />
      </RequireStaffCalendar>
    ),
  },
  {
    path: "/staff/booking-requests",
    element: <RequireClinician><StaffBookingRequestsPage /></RequireClinician>,
  },
  {
    path: "/staff/cancellation-requests",
    element: <RequireClinician><StaffCancellationRequestsPage /></RequireClinician>,
  },
  {
    path: "/staff/calendar",
    element: (
      <RequireStaffCalendar>
        <Navigate to="/staff/availability" replace />
      </RequireStaffCalendar>
    ),
  },
  {
    path: "/staff/clients",
    element: (
      <RequireClinician>
        {DESIGN_PREVIEW_MODE && PreviewStaffClientsPage
          ? <Suspense fallback={<div className="p-8">Loading design preview…</div>}><PreviewStaffClientsPage /></Suspense>
          : <ProductionStaffClientsPage />}
      </RequireClinician>
    ),
  },
  {
    path: "/staff/clients/:clientId",
    element: (
      <RequireStaffCalendar>
        {DESIGN_PREVIEW_MODE && PreviewClientDetailPage
          ? <Suspense fallback={<div className="p-8">Loading design preview…</div>}><PreviewClientDetailPage /></Suspense>
          : <CanonicalClientChartPage />}
      </RequireStaffCalendar>
    ),
  },
  {
    path: "/staff/clients/:clientId/note/:sessionId",
    element: (
      <RequireClinician>
        <NoteEditorPage />
      </RequireClinician>
    ),
  },
  {
    path: "/staff/clients/:clientId/treatment-plan",
    element: (
      <RequireClinician>
        {DESIGN_PREVIEW_MODE && PreviewTreatmentPlanEditorPage
          ? <Suspense fallback={<div className="p-8">Loading design preview…</div>}><PreviewTreatmentPlanEditorPage /></Suspense>
          : <ProductionTreatmentPlanEditorPage />}
      </RequireClinician>
    ),
  },
  {
    path: "/staff/clients/:clientId/clinical-document/:documentId",
    element: (
      <RequireClinician>
        <ClinicalTemplateDocumentPage />
      </RequireClinician>
    ),
  },
  {
    path: "/staff/clients/:clientId/discharge-summary",
    element: (
      <RequireClinician>
        <ProductionDischargeSummaryPage />
      </RequireClinician>
    ),
  },
  {
    path: "/staff/resources",
    element: (
      <RequireStaffCalendar>
        <StaffResourcesPage />
      </RequireStaffCalendar>
    ),
  },
  {
    path: "/staff/groups",
    element: (
      <RequireClinician>
        <StaffGroupsPage />
      </RequireClinician>
    ),
  },
  { path: "/staff/measures", element: <Navigate to="/staff" replace /> },
  { path: "/staff/documentation", element: <Navigate to="/staff" replace /> },
  {
    path: "/staff/clinicalarchitect",
    element: (
      <RequireClinician>
        {DESIGN_PREVIEW_MODE && PreviewClinicalArchitectPage
          ? <Suspense fallback={<div className="p-8">Loading design preview…</div>}><PreviewClinicalArchitectPage /></Suspense>
          : <ProductionClinicalArchitectPage />}
      </RequireClinician>
    ),
  },
  {
    path: "/staff/attendance",
    element: (
      <RequireClinician>
        <ClinicianAttendancePage />
      </RequireClinician>
    ),
  },

  // ─── Admin (top-level) ───────────────────────────────────
  {
    path: "/admin",
    element: (
      <RequireAdmin>
        <AdminDashboardPage />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/appointments",
    element: (
      <RequireAdmin>
        <AdminAppointmentsPage />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/documents",
    element: (
      <RequireAdmin>
        <AdminDocumentsPage />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/billing/records",
    element: <RequireAdmin><AdvancedBillingRecords/></RequireAdmin>,
  },
  {
    path: "/admin/billing",
    element: (
      <RequireAdmin>
        <AdminBillingPage />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/operations",
    element: (
      <RequireStaffCalendar>
        <AdminOperationsPage />
      </RequireStaffCalendar>
    ),
  },
  {
    path: "/admin/messages",
    element: (
      <RequireAdmin>
        <AdminMessagesPage />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/availability",
    element: (
      <RequireAdmin>
        <AdminAvailabilityPage />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/calendar",
    element: (
      <RequireAdmin>
        <Navigate to="/admin/availability" replace />
      </RequireAdmin>
    ),
  },
  // Consolidated into the canonical Availability workspace; retain safe bookmarks.
  {
    path: "/admin/open-slots",
    element: (
      <RequireAdmin>
        <Navigate to="/admin/availability" replace />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/availability-overview",
    element: (
      <RequireAdmin>
        <Navigate to="/admin/availability" replace />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/booking-visibility",
    element: (
      <RequireAdmin>
        {DESIGN_PREVIEW_MODE && PreviewBookingVisibilityPage
          ? <Suspense fallback={<div className="p-8">Loading design preview…</div>}><PreviewBookingVisibilityPage /></Suspense>
          : <Navigate to="/admin/practice-settings/services" replace />}
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/roles",
    element: (
      <RequireAdmin>
        <AdminRolesPage />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/document-templates",
    element: <Navigate to="/admin/forms" replace />,
  },
  {
    path: "/staff/internal-surveys",
    element: <RequireStaffCalendar><ClientSurveyPage audience="staff" /></RequireStaffCalendar>,
  },
  {
    path: "/admin/forms",
    element: (
      <RequireAdmin>
        <AdminFormsPage />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/forms/builder",
    element: (
      <RequireAdmin>
        <FormBuilderPage />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/forms/survey-builder",
    element: (
      <RequireAdmin>
        <Navigate to="/admin/forms/builder" replace />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/staff-profiles",
    element: (
      <RequireAdmin>
        <AdminStaffProfilesPage />
      </RequireAdmin>
    ),
  },
  // Alias: "/admin/staff" redirects to "/admin/staff-profiles"
  {
    path: "/admin/staff",
    element: <Navigate to="/admin/staff-profiles" replace />,
  },
  {
    path: "/admin/service-settings",
    element: (
      <RequireSuperAdmin>
        {DESIGN_PREVIEW_MODE && PreviewServiceSettingsPage
          ? <Suspense fallback={<div className="p-8">Loading design preview…</div>}><PreviewServiceSettingsPage /></Suspense>
          : <Navigate to="/admin/practice-settings/services" replace />}
      </RequireSuperAdmin>
    ),
  },
  {
    path: "/admin/clients",
    element: (
      <RequireAdmin>
        <AdminClientRegistryPage />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/clients/:clientId/chart",
    element: <RequireAdmin><LegacyAdminClientChartRedirect /></RequireAdmin>,
  },
  {
    path: "/admin/groups",
    element: (
      <RequireAdmin>
        <AdminGroupManagementPage />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/attendance",
    element: (
      <RequireAdmin>
        {DESIGN_PREVIEW_MODE && PreviewAttendanceTotalsPage
          ? <Suspense fallback={<div className="p-8">Loading design preview…</div>}><PreviewAttendanceTotalsPage /></Suspense>
          : <ProductionAttendanceTotalsPage />}
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/practice-settings",
    element: (
      <RequireSuperAdmin>
        <PracticeSettingsPage />
      </RequireSuperAdmin>
    ),
  },
  {
    path: "/admin/practice-settings/services",
    element: (
      <RequireAdmin>
        <PracticeSettingsPage restrictedSection="services" />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/claim-drafts",
    element: (
      <RequireAdmin>
        <ClaimDraftsPage />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/practice-settings/reminders",
    element: (
      <RequireSuperAdmin>
        <PracticeSettingsPage restrictedSection="reminders" />
      </RequireSuperAdmin>
    ),
  },
  {
    path: "/admin/reminder-settings",
    element: (
      <RequireSuperAdmin>
        <Navigate to="/admin/practice-settings/reminders" replace />
      </RequireSuperAdmin>
    ),
  },
  {
    path: "/admin/gfe-report",
    element: (
      <RequireAdmin>
        <Navigate to="/admin/documents" replace />
      </RequireAdmin>
    ),
  },
  {
    path: "/admin/activity-log",
    element: (
      <RequireAdmin>
        <AdminActivityLogPage />
      </RequireAdmin>
    ),
  },

  // ─── Marketing (top-level) ──────────────────────────────
  {
    path: "/marketing",
    element: (
      <RequireMarketing>
        <MarketingDashboardPage />
      </RequireMarketing>
    ),
  },
  {
    path: "/marketing/subscribers",
    element: <RequireMarketing><Navigate to="/marketing" replace /></RequireMarketing>,
  },
  {
    path: "/marketing/campaigns",
    element: <RequireMarketing><Navigate to="/marketing" replace /></RequireMarketing>,
  },
  {
    path: "/marketing/funnel-performance",
    element: <RequireMarketing><Navigate to="/marketing" replace /></RequireMarketing>,
  },
  {
    path: "/marketing/ghl-sync",
    element: <RequireMarketing><Navigate to="/marketing" replace /></RequireMarketing>,
  },

  // ─── 404 ─────────────────────────────────────────────────
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
